import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, Mail, Send, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const KPI_BLOCKS = [
  { key: "fridge_total", label: "Fridge checks", suffix: "all-time", testid: "kpi-fridge" },
  { key: "audits_open", label: "Open audits", suffix: "to close", testid: "kpi-audits" },
  { key: "events_upcoming", label: "Upcoming events", suffix: "scheduled", testid: "kpi-events" },
  { key: "invoices_unpaid", label: "Unpaid invoices", suffix: "in flight", testid: "kpi-invoices" },
  { key: "expiring_soon", label: "Batches ≤ 7d", suffix: "expiring", testid: "kpi-expiring" },
  { key: "training_expiring", label: "Certs ≤ 7d", suffix: "to renew", testid: "kpi-training" },
];

const THRESHOLD_KEY = "hearth.recurring_threshold";

export default function Dashboard() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [data, setData] = useState(null);
  const [threshold, setThreshold] = useState(() => {
    const stored = parseInt(localStorage.getItem(THRESHOLD_KEY) || "3", 10);
    return Number.isFinite(stored) && stored >= 1 && stored <= 10 ? stored : 3;
  });
  const [recurring, setRecurring] = useState({ threshold: 3, items: [] });
  const [digestInfo, setDigestInfo] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [editingDigestEmail, setEditingDigestEmail] = useState(false);
  const [digestEmailInput, setDigestEmailInput] = useState("");
  const [savingDigestEmail, setSavingDigestEmail] = useState(false);

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
  }, []);

  useEffect(() => {
    localStorage.setItem(THRESHOLD_KEY, String(threshold));
    api
      .get(`/dashboard/recurring-failures?threshold=${threshold}`)
      .then((r) => setRecurring(r.data))
      .catch(() => setRecurring({ threshold, items: [] }));
  }, [threshold]);

  useEffect(() => {
    if (!isOwner) return;
    api
      .get("/notifications/preview-digest")
      .then((r) => setDigestInfo(r.data))
      .catch(() => setDigestInfo({ configured: false }));
  }, [isOwner]);

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data: res } = await api.post("/notifications/test-digest");
      if (res.sent) {
        toast.success(`Digest sent to ${res.to}`);
      } else {
        toast.message(res.reason || "Digest skipped");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSendingTest(false);
    }
  };

  const startEditDigest = () => {
    setDigestEmailInput(digestInfo?.configured_digest_email || "");
    setEditingDigestEmail(true);
  };

  const cancelEditDigest = () => {
    setEditingDigestEmail(false);
    setDigestEmailInput("");
  };

  const saveDigestEmail = async () => {
    setSavingDigestEmail(true);
    try {
      const { data: res } = await api.put("/notifications/digest-email", {
        digest_email: digestEmailInput.trim(),
      });
      setDigestInfo((prev) => ({
        ...(prev || {}),
        to: res.effective_to,
        configured_digest_email: res.digest_email,
        source: res.digest_email ? "business_configured" : "owner_login_email",
      }));
      setEditingDigestEmail(false);
      toast.success(
        res.digest_email
          ? `Digest will now be sent to ${res.digest_email}`
          : "Reverted — digest will go to your login email"
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save");
    } finally {
      setSavingDigestEmail(false);
    }
  };

  const kpis = data?.kpis || {};
  const alerts = data?.alerts || [];
  const trend = data?.fridge_trend || [];

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-[1500px]" data-testid="dashboard-page">
      <PageHeader
        eyebrow={today}
        title={`Good day, ${user?.name?.split(" ")[0] || "Owner"}.`}
        description="A snapshot of compliance, money in flight, and what's expiring next."
        testid="dashboard-header"
      />

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-[#e5e2d9] bg-white">
        {KPI_BLOCKS.map((k, i) => (
          <div
            key={k.key}
            data-testid={k.testid}
            className={`p-5 animate-fade-up delay-${(i % 5) + 1} ${
              i < KPI_BLOCKS.length - 1 ? "border-r border-[#e5e2d9]" : ""
            } ${i >= 3 ? "border-t lg:border-t-0" : ""} border-[#e5e2d9]`}
          >
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">{k.label}</div>
            <div className="font-display text-4xl mt-3 leading-none">
              {kpis[k.key] ?? "—"}
            </div>
            <div className="text-xs text-[#8a8476] mt-2">{k.suffix}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        {/* Trend */}
        <div className="lg:col-span-2 bg-white border border-[#e5e2d9] p-6 animate-fade-up delay-2">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">
                30-day fridge log activity
              </div>
              <h3 className="font-display text-2xl mt-2">Compliance trend</h3>
            </div>
            <div className="text-xs text-[#57534e]">
              <span className="inline-block w-2 h-2 bg-[#1b3b36] mr-2" />Checks
              <span className="inline-block w-2 h-2 bg-[#d94f3d] ml-4 mr-2" />Fails
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1b3b36" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#1b3b36" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#efece6" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#8a8476", fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: "#e5e2d9" }}
                  tickFormatter={(d) => d?.slice(5)}
                />
                <YAxis tick={{ fill: "#8a8476", fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "#ffffff",
                    border: "1px solid #e5e2d9",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                />
                <Area type="monotone" dataKey="total" stroke="#1b3b36" strokeWidth={1.8} fill="url(#g1)" />
                <Area type="monotone" dataKey="fails" stroke="#d94f3d" strokeWidth={1.5} fill="transparent" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white border border-[#e5e2d9] p-6 animate-fade-up delay-3" data-testid="alerts-panel">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">
                Attention needed
              </div>
              <h3 className="font-display text-2xl mt-2">Action queue</h3>
            </div>
            <StatusPill tone={alerts.length ? "alert" : "success"}>
              {alerts.length || 0}
            </StatusPill>
          </div>
          <div className="space-y-3">
            {alerts.length === 0 && (
              <div className="text-sm text-[#8a8476] py-8 text-center" data-testid="alerts-empty">
                All clear. Nothing pressing right now.
              </div>
            )}
            {alerts.map((a, idx) => (
              <div
                key={idx}
                data-testid={`alert-${a.type}-${idx}`}
                className="flex gap-3 py-3 border-b border-[#efece6] last:border-0"
              >
                <div
                  className={`w-1 self-stretch ${
                    a.severity === "alert" ? "bg-[#d94f3d]" : "bg-[#d9953d]"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <StatusPill tone={a.severity}>{a.type}</StatusPill>
                  </div>
                  <div className="text-sm text-[#1c1917] mt-1.5 truncate">{a.title}</div>
                  <div className="text-xs text-[#8a8476] mt-0.5">{a.subtitle}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recurring failures + Notifications */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Recurring failures */}
        <div className="lg:col-span-2 bg-white border border-[#e5e2d9] p-6 animate-fade-up delay-4" data-testid="recurring-failures-panel">
          <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">
                Audit trend
              </div>
              <h3 className="font-display text-2xl mt-2 inline-flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#d9953d]" />
                Recurring failures
              </h3>
              <div className="text-xs text-[#57534e] mt-1">
                Checks that have failed in the last <span className="font-mono text-[#1b3b36]">{threshold}</span> consecutive audits.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-[0.18em] text-[#8a8476]">Threshold</label>
              <Input
                data-testid="recurring-threshold-input"
                type="number"
                min={1}
                max={20}
                value={threshold}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (Number.isFinite(v) && v >= 1 && v <= 20) setThreshold(v);
                }}
                className="w-16 h-9 text-center bg-white rounded-[4px] font-mono"
              />
            </div>
          </div>
          <div className="space-y-2" data-testid="recurring-failures-list">
            {recurring.items.length === 0 && (
              <div className="text-sm text-[#4e7b5b] py-8 text-center inline-flex items-center justify-center gap-2 w-full" data-testid="recurring-failures-empty">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4e7b5b]" />
                No checks have failed {threshold}× in a row. Nice work.
              </div>
            )}
            {recurring.items.map((r, idx) => (
              <div
                key={idx}
                data-testid={`recurring-failure-${idx}`}
                className="flex gap-3 py-3 border-b border-[#efece6] last:border-0"
              >
                <div className="w-1 self-stretch bg-[#d94f3d]" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusPill tone="alert">{r.fail_streak}× failed</StatusPill>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#8a8476]">
                      last {r.last_failure_date}
                    </span>
                  </div>
                  <div className="text-sm text-[#1c1917] mt-1.5">{r.item_text}</div>
                  {r.last_failure_note && (
                    <div className="text-xs text-[#b9531c] mt-1 italic">
                      Last note: {r.last_failure_note}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notifications card (owner only) */}
        {isOwner && (
          <div className="bg-white border border-[#e5e2d9] p-6 animate-fade-up delay-5" data-testid="notifications-panel">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">
              Email notifications
            </div>
            <h3 className="font-display text-2xl mt-2 inline-flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#1b3b36]" />
              Daily digest
            </h3>
            {digestInfo ? (
              <>
                <div className="text-xs text-[#57534e] mt-2 leading-relaxed">
                  {digestInfo.configured ? (
                    <>
                      Sent to{" "}
                      {editingDigestEmail ? (
                        <span className="inline-flex items-center gap-1 align-middle">
                          <Input
                            data-testid="digest-email-input"
                            type="email"
                            value={digestEmailInput}
                            onChange={(e) => setDigestEmailInput(e.target.value)}
                            placeholder={digestInfo.owner_login_email || "kitchen@yourbiz.co"}
                            className="h-7 py-1 text-xs bg-white rounded-[3px] inline-block w-64 font-mono"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveDigestEmail();
                              if (e.key === "Escape") cancelEditDigest();
                            }}
                          />
                          <button
                            type="button"
                            data-testid="digest-email-save"
                            onClick={saveDigestEmail}
                            disabled={savingDigestEmail}
                            className="p-1 text-[#4e7b5b] hover:text-[#2c5a53] disabled:opacity-40"
                            aria-label="Save digest email"
                          >
                            <Check className="w-3.5 h-3.5" strokeWidth={2.2} />
                          </button>
                          <button
                            type="button"
                            data-testid="digest-email-cancel"
                            onClick={cancelEditDigest}
                            disabled={savingDigestEmail}
                            className="p-1 text-[#8a8476] hover:text-[#d94f3d]"
                            aria-label="Cancel"
                          >
                            <X className="w-3.5 h-3.5" strokeWidth={2.2} />
                          </button>
                        </span>
                      ) : (
                        <>
                          <span data-testid="digest-current-email" className="font-mono text-[#1c1917]">{digestInfo.to || "—"}</span>
                          <button
                            type="button"
                            data-testid="digest-email-edit"
                            onClick={startEditDigest}
                            className="ml-1.5 p-1 text-[#8a8476] hover:text-[#1b3b36] align-middle"
                            aria-label="Change digest recipient email"
                            title="Change digest recipient"
                          >
                            <Pencil className="w-3 h-3" strokeWidth={2} />
                          </button>
                        </>
                      )}
                      {" "}at <span className="font-mono">{String(digestInfo.scheduled_hour_utc ?? 8).padStart(2, "0")}:00 UTC</span> daily.
                      {digestInfo.source === "business_configured" && !editingDigestEmail && (
                        <div className="mt-1 text-[10px] text-[#8a8476]">Overriding your login email — clear this field to revert to <span className="font-mono">{digestInfo.owner_login_email}</span>.</div>
                      )}
                    </>
                  ) : (
                    <span className="text-[#b9531c]">Email service not configured. Ask your admin to set BREVO_API_KEY.</span>
                  )}
                </div>
                {digestInfo.counts && (
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center" data-testid="digest-counts">
                    <div className="border border-[#e5e2d9] py-2">
                      <div className="font-mono text-lg text-[#1b3b36]">{digestInfo.counts.batches}</div>
                      <div className="text-[9px] uppercase tracking-[0.18em] text-[#8a8476] mt-0.5">batches</div>
                    </div>
                    <div className="border border-[#e5e2d9] py-2">
                      <div className="font-mono text-lg text-[#1b3b36]">{digestInfo.counts.audits}</div>
                      <div className="text-[9px] uppercase tracking-[0.18em] text-[#8a8476] mt-0.5">audits</div>
                    </div>
                    <div className="border border-[#e5e2d9] py-2">
                      <div className="font-mono text-lg text-[#1b3b36]">{digestInfo.counts.events}</div>
                      <div className="text-[9px] uppercase tracking-[0.18em] text-[#8a8476] mt-0.5">events</div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-[#8a8476] mt-2">Loading…</div>
            )}
            <Button
              data-testid="notifications-test-button"
              onClick={sendTest}
              disabled={sendingTest || !digestInfo?.configured}
              className="w-full mt-4 bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingTest ? "Sending…" : "Send test digest now"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
